import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Figure, Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import {
  revenueByStore,
  previousWindowDays,
  depositsReceivedIn,
  depositPool,
  topupReport,
  merchByCategory,
} from '@/domain/reportingEngine';

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

const AGING_META: Record<string, { label: string; flag?: string }> = {
  pastValidity: { label: 'Past 12 months', flag: 'action' },
  le30: { label: 'Expiring ≤ 30 days', flag: 'action' },
  d31to90: { label: '31 – 90 days' },
  d91plus: { label: '91 days – 12 months' },
};

const TOPUP_DAYS_META: Record<string, string> = {
  le7: '0 – 7 days',
  d8to21: '8 – 21 days',
  d22plus: '22+ days',
};

interface RevenueTotals {
  oursAud: number;
  artistAud: number;
  totalAud: number;
}

/** Sum `revenueByStore` rows into a single ours/artist/total figure. */
function revenueTotalsFor(days: readonly string[], sid: StudioId | undefined): RevenueTotals {
  return revenueByStore(days, sid).reduce(
    (acc, r) => ({ oursAud: acc.oursAud + r.oursAud, artistAud: acc.artistAud + r.artistAud, totalAud: acc.totalAud + r.totalAud }),
    { oursAud: 0, artistAud: 0, totalAud: 0 },
  );
}

/** Percentage change vs the previous equal-length period, with an up/down tone; undefined when there's no base. */
function periodDelta(current: number, previous: number): { caption: string; tone: 'up' | 'down' } | undefined {
  if (previous <= 0) return undefined;
  const change = ((current - previous) / previous) * 100;
  return { caption: `${change >= 0 ? '+' : ''}${change.toFixed(1)}% vs previous period`, tone: change >= 0 ? 'up' : 'down' };
}

/**
 * Money tab (leak-first reporting). Owner-only: what did we make, and is
 * deposit money staying in the bank? Turnover split ours-vs-artist with a
 * period-over-period delta, money-in (new deposits + merch), the live held
 * deposit pool with expiry aging, the top-up book, merch by category, and
 * per-store totals - or a compare table across studios when several are
 * selected. Every dollar renders via `<Money>` regardless of visibility, so
 * the shell's owner-only mount is the only gate.
 */
export default function MoneyTab({ scope }: { scope: ReportingScope }) {
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const merchItems = useBrainStore((s) => s.merchItems);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const single = singleStudioScope(studioIds);
  const prevDays = useMemo(() => previousWindowDays(days), [days]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const perStore = useMemo(
    () => revenueByStore(days, single),
    [days, single, bookings, salePayments, artists, studios],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totals = useMemo(
    () => revenueTotalsFor(days, single),
    [days, single, bookings, salePayments, artists, studios],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prevTotals = useMemo(
    () => revenueTotalsFor(prevDays, single),
    [prevDays, single, bookings, salePayments, artists, studios],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depIn = useMemo(
    () => depositsReceivedIn(days, single),
    [days, single, deposits, customers, studios],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const merchCats = useMemo(
    () => merchByCategory(days, single),
    [days, single, salePayments, merchItems, studios],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pool = useMemo(
    () => depositPool(single, { rangeDays: days }),
    [single, days, deposits, bookings],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const topups = useMemo(() => topupReport(single), [single, deposits, bookings, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => revenueTotalsFor(days, sid)) : []),
    [compareMode, studioIds, days, bookings, salePayments, artists, studios],
  );

  const merchTotal = merchCats.reduce((s, c) => s + c.totalAud, 0);
  const maxAging = Math.max(1, ...pool.aging.map((a) => a.amountAud));
  const maxCat = Math.max(1, ...merchCats.map((c) => c.totalAud));
  const housePct = totals.totalAud > 0 ? totals.oursAud / totals.totalAud : 0;

  const turnoverDelta = periodDelta(totals.totalAud, prevTotals.totalAud);
  const oursDelta = periodDelta(totals.oursAud, prevTotals.oursAud);
  const artistDelta = periodDelta(totals.artistAud, prevTotals.artistAud);

  if (compareMode) {
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-money">
        <section className="panel p-4 overflow-x-auto">
          <h2 className="heading-serif text-lg text-cream mb-4">Money, studios compared</h2>
          <table className="w-full text-sm" data-testid="money-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Metric</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">
                    {studios[s.studioId]?.name ?? s.studioId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Turnover</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    <Money amountAud={s.value.totalAud} visible={moneyVisible} />
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Ours (house)</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-gold-bright">
                    <Money amountAud={s.value.oursAud} visible={moneyVisible} />
                  </td>
                ))}
              </tr>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Artists</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream-soft">
                    <Money amountAud={s.value.artistAud} visible={moneyVisible} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-money">
      {/* Turnover split, ours vs artist, with a period-over-period delta */}
      <section className="grid grid-cols-3 gap-3">
        <Tile testId="money-tile-turnover" label="Turnover" caption={turnoverDelta?.caption} tone={turnoverDelta?.tone}>
          <Money amountAud={totals.totalAud} visible={moneyVisible} />
        </Tile>
        <Tile
          testId="money-tile-ours"
          label="Ours (house)"
          info={`${totals.totalAud > 0 ? pct(housePct) : '—'} of turnover`}
          caption={oursDelta?.caption}
          tone={oursDelta?.tone}
        >
          <Money amountAud={totals.oursAud} visible={moneyVisible} />
        </Tile>
        <Tile testId="money-tile-artists" label="Artists" caption={artistDelta?.caption} tone={artistDelta?.tone}>
          <Money amountAud={totals.artistAud} visible={moneyVisible} />
        </Tile>
      </section>

      {/* Money in: new deposits + merch, over the range */}
      <section className="grid grid-cols-2 gap-3">
        <Tile label="New deposits received" info="New money in via deposits over the range - not revenue.">
          <Money amountAud={depIn.totalAud} visible={moneyVisible} />
        </Tile>
        <Tile label="Merch & aftercare" info="Merch and aftercare sold in the range - earned, all house, never split with artists.">
          <Money amountAud={merchTotal} visible={moneyVisible} />
        </Tile>
      </section>

      {/* Held deposit pool - a live liability snapshot, plus expiry aging */}
      <section className="panel p-4" data-testid="money-deposit-pool">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Held deposit pool
          <InfoTip>
            Every deposit still holding the client&rsquo;s money as of today - a live liability snapshot, not a
            ranged figure. Money on a booking is one line. Only deposits on file with no date age, against their
            12 months; past 12 months and the nearest bucket are the action list.
          </InfoTip>
        </h2>
        <p className="font-cinzel text-3xl text-cream mt-1">
          <Figure testId="money-pool-held" amountAud={pool.heldAud} count={pool.count}>
            <Money amountAud={pool.heldAud} visible={moneyVisible} />
          </Figure>
        </p>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">{pool.count} outstanding</p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-cream-soft">
          <span>
            Booked in{' '}
            <Figure testId="money-pool-booked-in" amountAud={pool.bookedInAud} count={pool.bookedInCount}>
              <Money amountAud={pool.bookedInAud} visible={moneyVisible} />
            </Figure>{' '}
            <span className="text-cream-faint">· {pool.bookedInCount}</span>
          </span>
          <span>
            On file, no date{' '}
            <Figure testId="money-pool-on-file" amountAud={pool.onFileAud} count={pool.onFileCount}>
              <Money amountAud={pool.onFileAud} visible={moneyVisible} />
            </Figure>{' '}
            <span className="text-cream-faint">· {pool.onFileCount}</span>
          </span>
          <span title="Still linked to a cancelled or no-show booking: keep on file or forfeit">
            Needs a decision{' '}
            <Figure testId="money-pool-needs-decision" amountAud={pool.needsDecisionAud} count={pool.needsDecisionCount}>
              <Money amountAud={pool.needsDecisionAud} visible={moneyVisible} />
            </Figure>{' '}
            <span className="text-cream-faint">· {pool.needsDecisionCount}</span>
          </span>
        </div>
        <p className="mt-4 label-small-caps">On file, by time left of 12 months</p>
        <div className="mt-2 space-y-1.5">
          {pool.aging.map((a) => {
            const meta = AGING_META[a.key]!;
            return (
              <div key={a.key} className="flex items-center gap-3">
                <span className="w-44 text-xs text-cream-soft flex items-center gap-1.5 shrink-0">
                  {meta.label}
                  {meta.flag ? (
                    <span className="text-[9px] uppercase tracking-wider text-blood border border-blood/50 rounded px-1">
                      {meta.flag}
                    </span>
                  ) : null}
                </span>
                <span className="flex-1 h-2.5 rounded-full bg-ink-soft/40 overflow-hidden">
                  <span className="block h-full rounded-full bg-gold" style={{ width: `${(a.amountAud / maxAging) * 100}%` }} />
                </span>
                <span className="font-mono text-xs text-cream shrink-0 w-28 text-right">
                  <Figure testId={`money-pool-bucket-${a.key}`} amountAud={a.amountAud} count={a.count}>
                    <Money amountAud={a.amountAud} visible={moneyVisible} />
                  </Figure>{' '}
                  <span className="text-cream-faint">· {a.count}</span>
                </span>
              </div>
            );
          })}
        </div>
        {pool.forfeitedInRangeAud > 0 ? (
          <p className="mt-3 text-[11px] text-cream-soft">
            Expired / forfeited in range: <Money amountAud={pool.forfeitedInRangeAud} visible={moneyVisible} />{' '}
            <span className="text-cream-faint">· {pool.forfeitedInRangeCount} moved to house</span>
          </p>
        ) : null}
      </section>

      {/* Top-ups: the open balance book, a now snapshot like the pool */}
      <section className="panel p-4" data-testid="money-topups">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Top-ups
          <InfoTip>
            Partial deposits with a balance still owing. A now snapshot of the reminder track, not a ranged figure.
          </InfoTip>
        </h2>
        {topups.count === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No top-ups on the book - every deposit is paid in full.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <Tile label="On the book">{topups.count.toLocaleString('en-AU')}</Tile>
              <Tile label="Outstanding">
                <Money amountAud={topups.outstandingAud} visible={moneyVisible} />
              </Tile>
              <Tile label="Close to session">{topups.closeToSession.toLocaleString('en-AU')}</Tile>
              <Tile label="Need a nudge">{topups.needNudge.toLocaleString('en-AU')}</Tile>
            </div>
            <p className="label-small-caps mt-5 mb-2">By days to session</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-faint">
                  <th className="text-left py-1.5 font-normal">Days to session</th>
                  <th className="text-right py-1.5 font-normal">Count</th>
                  <th className="text-right py-1.5 font-normal">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {topups.byDays.map((b) => (
                  <tr key={b.key} className="border-t border-gold-dim/15" data-testid={`money-topups-days-${b.key}`}>
                    <td className="py-1.5 text-cream">{TOPUP_DAYS_META[b.key]}</td>
                    <td className="py-1.5 text-right font-mono text-cream-soft">{b.count}</td>
                    <td className="py-1.5 text-right font-mono text-cream-soft">
                      <Money amountAud={b.amountAud} visible={moneyVisible} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* Merch by category */}
      <section className="panel p-4" data-testid="money-merch">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Merch by category
          <InfoTip>Merch and aftercare sold in the range, grouped by catalogue category, largest first.</InfoTip>
        </h2>
        <div className="space-y-2">
          {merchCats.length === 0 ? (
            <p className="text-xs text-cream-faint">No merch sold in this range.</p>
          ) : (
            merchCats.map((c) => (
              <div key={c.category} className="flex items-center gap-3">
                <span className="w-24 text-xs text-cream-soft capitalize shrink-0">{c.category}</span>
                <span className="flex-1 h-2.5 rounded-full bg-ink-soft/40 overflow-hidden">
                  <span className="block h-full rounded-full bg-gold" style={{ width: `${(c.totalAud / maxCat) * 100}%` }} />
                </span>
                <span className="font-mono text-xs text-cream shrink-0 w-28 text-right">
                  <Money amountAud={c.totalAud} visible={moneyVisible} /> <span className="text-cream-faint">· {c.qty}</span>
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Per-store totals over the range, with a totals footer */}
      <section className="panel p-4" data-testid="money-by-store">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Per-store totals
          <InfoTip>
            Each studio&rsquo;s completed-session revenue over the range - turnover, the house share and the artist
            envelope, and house% of turnover. Largest turnover first.
          </InfoTip>
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Studio</th>
                <th className="text-right px-2 py-2">Turnover</th>
                <th className="text-right px-2 py-2">Ours</th>
                <th className="text-right px-2 py-2">Artists</th>
                <th className="text-right px-2 py-2">House %</th>
              </tr>
            </thead>
            <tbody>
              {perStore.length === 0 ? (
                <tr>
                  <td className="px-2 py-3 text-cream-faint" colSpan={5}>
                    No studios in scope.
                  </td>
                </tr>
              ) : (
                perStore.map((s) => (
                  <tr key={s.studioId} className="border-t border-gold-dim/15" data-testid={`money-store-${s.studioId}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{s.name}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream">
                      <Money amountAud={s.totalAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">
                      <Money amountAud={s.oursAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">
                      <Money amountAud={s.artistAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">
                      {s.totalAud > 0 ? pct(s.oursAud / s.totalAud) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-gold-dim/30 text-cream">
                <td className="px-2 py-2 label-small-caps text-gold-bright">All studios</td>
                <td className="px-2 py-2 text-right font-mono">
                  <Money amountAud={totals.totalAud} visible={moneyVisible} />
                </td>
                <td className="px-2 py-2 text-right font-mono text-gold-bright">
                  <Money amountAud={totals.oursAud} visible={moneyVisible} />
                </td>
                <td className="px-2 py-2 text-right font-mono text-cream-soft">
                  <Money amountAud={totals.artistAud} visible={moneyVisible} />
                </td>
                <td className="px-2 py-2 text-right font-mono text-cream-soft">
                  {totals.totalAud > 0 ? pct(housePct) : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
}

function Tile({
  label,
  info,
  caption,
  tone,
  testId,
  children,
}: {
  label: string;
  info?: string;
  caption?: string;
  tone?: 'up' | 'down';
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className="panel-dark p-4" data-testid={testId}>
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
      {caption ? (
        <p
          className={`font-inter text-[10px] uppercase tracking-widest mt-1 ${
            tone === 'down' ? 'text-blood' : tone === 'up' ? 'text-mint' : 'text-cream-faint'
          }`}
        >
          {caption}
        </p>
      ) : null}
    </div>
  );
}
